import type { ToastProps } from '#types/notification'

export interface Toast { id?: string, props?: ToastProps, autoClose?: boolean, delay?: number, promise?: boolean }

const toasts = ref<Toast[]>([])

export default createGlobalState(() => {
  const { toasterDismissDelay } = useAppConfig()
  const INTERVAL = 200
  const _delay = ref(toasterDismissDelay)
  const timers: Record<string, number> = {}
  const promises: Record<string, (value: void) => void> = {}

  const { pause, resume } = useIntervalFn(() => {
    Object.keys(timers).forEach((key) => {
      timers[key] -= INTERVAL
      if (timers[key] <= 0)
        remove(key)
    })
  }, INTERVAL)

  function add({ props, autoClose = true, delay, promise, id: passedId }: Toast = {}) {
    const id = passedId ?? `toast_${getIdClient()}`
    if (autoClose) {
      _delay.value = delay || toasterDismissDelay
      timers[id] = _delay.value
    }

    toasts.value.push({ props, id })

    if (promise) {
      const resolvers = promiseWithResolvers()
      promises[id] = resolvers.resolve
      return resolvers.promise
    }
  }

  function remove(id) {
    toasts.value = toasts.value.filter((toast) => toast.id !== id)
    delete timers[id]
    promises[id]?.(void 0)
  }

  const stopWatch = watchEffect(() => {
    if (!_delay.value) {
      pause()
      nextTick(() => stopWatch())
      return
    }
    if (!toasts.value.length)
      pause()
    else resume()
  })

  return {
    toasts: readonly(toasts),
    add,
    remove,
    pause,
    resume
  }
})
